import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Breadcrumb, Collapse, Divider, Form, message, Modal, Spin, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { ReactComponent as RebaseIcon } from "../../../../img/rebase-icon.svg";
import { ReactComponent as AdjustmentIcon } from "../../../../img/adjustment-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../../img/delete-round.svg";
import { ReactComponent as PlusIcon } from "../../../../img/plus.svg";
import {
  checkIsLoading,
  combineClasses,
  getAccountFullName,
  toHttpFormat,
  toHttpFormatStartOfDay,
  useActiveModal,
} from "../../../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../../..";
import { Account } from "../../../../lib/interfaces";
import {
  accountPaymentDetailsActions,
  fetchPayment,
  fetchPaymentAdjustments,
} from "../../model/slice/accountPaymentDetailsSlice";
import { AccountPaymentDetails } from "../../entities/AccountPaymentDetails/AccountPaymentDetails";
import { useDispatchWithCheck } from "../../../../lib/hooks/useDispatchWithCheck";
import {
  fetchDicAdjustmentReason,
  fetchDicAdjustmentType,
  fetchPaymentSourceTypes,
  fetchPaymentStatuses,
} from "../../../../store/slices/rtkDictionaries";
import { AccountPaymentGoals } from "../../entities/AccountPaymentGoals/AccountPaymentGoals";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ACCOUNT_PAYMENTS_WRITE } from "../../../../lib/const/permissions";
import { PaymentActiveModal } from "../../model/types/PaymentActiveModal";
import { PaymentTransferModal } from "../../features/PaymentTransferModal/PaymentTransferModal";
import { AgreementForPaymentRebase } from "../../model/types/AgreementForPaymentRebase";
import { baseUrl } from "../../../../lib/routes/baseUrl";
import { useLoading } from "../../../../lib/hooks/useLoading";
import { PaymentCancelModal } from "../../features/PaymentCancelModal/PaymentCancelModal";
import { AdjustmentsModal } from "../../features/AdjustmentsModal/AdjustmentsModal";
import buildUrl from "../../../../lib/utils/buildUrl";
import { CreateAdjustmentsModal } from "../../features/CreateAdjustmentsModal/CreateAdjustmentsModal";
import {
  CreateAdjustmentData,
  CreateAdjustmentFormData,
} from "../../model/types/CreateAdjustmentData";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";
import { fetchPaymentGoalTypes } from "../../../../store/slices/dicts";
import { CreateGoalModal } from "../../features/CreateGoalModal/CreateGoalModal";
import { UncoverGoalBody } from "../../model/types/UncoverGoalBody";
import { isPayment } from "../../../../lib/typeGuards/isPayment";
import { CreateGoalBillData } from "../../model/types/CreateGoalBillData";
import { CreateGoalOfferData } from "../../model/types/CreateGoalOfferData";
import { CreateGoalServiceData } from "../../model/types/CreateGoalServiceData";
import "./AccountPaymentDetailsPage.less";

interface ComponentProps {
  className?: string;
}

export const AccountPaymentDetailsPage = (props: ComponentProps) => {
  const { className } = props;
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { dispatchWithCheck } = useDispatchWithCheck();
  const { paymentId } = useParams<{ accountId?: string; paymentId?: string }>();
  const account = useAppSelector((state) => state.accounts.account) as Account;
  const { payment, paymentLoading, paymentAdjustments, paymentAdjustmentsLoading } = useAppSelector(
    (state) => state.accountPaymentDetails
  );
  const fullName = getAccountFullName(account);

  const { isLoading, startLoading, stopLoading } = useLoading();

  const {
    paymentStatusesLoading,
    paymentSourceTypesLoading,
    dicAdjustmentTypeLoading,
    dicAdjustmentReasonLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const { paymentGoalTypesLoading } = useAppSelector((state) => state.dicts);

  const fetchPaymentAndAdjustments = (id: string) => {
    dispatch(fetchPayment(id)).then(({ payload }) => {
      if (isPayment(payload)) {
        dispatch(
          fetchPaymentAdjustments({
            agreementId: payload.agreement.id,
            parentTransactionId: payload.transactionId,
          })
        );
      }
    });
  };

  // Загрузка платежа и начислений
  useEffect(() => {
    if (paymentId) {
      fetchPaymentAndAdjustments(paymentId);
    }
    return () => {
      dispatch(accountPaymentDetailsActions.cleanPayment());
      dispatch(accountPaymentDetailsActions.cleanPaymentAdjustments());
    };
  }, [paymentId]);

  // Загрузка словарей, в случае незагруженности
  useEffect(() => {
    dispatchWithCheck(fetchPaymentSourceTypes(), paymentSourceTypesLoading);
    dispatchWithCheck(fetchPaymentStatuses(), paymentStatusesLoading);
    dispatchWithCheck(fetchDicAdjustmentType(), dicAdjustmentTypeLoading);
    dispatchWithCheck(fetchDicAdjustmentReason(), dicAdjustmentReasonLoading);
    dispatchWithCheck(fetchPaymentGoalTypes(), paymentGoalTypesLoading);
  }, []);

  const { activeModal, setActiveModal } = useActiveModal<PaymentActiveModal>("");
  const closeActiveModal = () => {
    setActiveModal("");
  };
  const [agreementForRebase, setAgreementForRebase] = useState<AgreementForPaymentRebase>();
  const [clickNextHasModal, setClickNextHasModal] = useState(false);

  const [createAdjusmentForm] = Form.useForm<CreateAdjustmentFormData>();
  const [createPaymentTransferForm] = Form.useForm();

  const paymentData = useMemo(() => {
    if (!payment) {
      return undefined;
    }
    const amount = payment?.amount ? Number(payment.amount) : undefined;
    const availableAmount = payment?.extra?.availableAmount
      ? Number(payment.extra.availableAmount)
      : undefined;

    const hasAdjusment = paymentAdjustments?.some(
      ({ parentTransactionId }) => payment.transactionId === parentTransactionId
    );

    const confirmDate = moment(payment.confirmDate);
    const paymentDate = moment(payment.paymentDate);

    let minTopLimit = 0;
    if (amount && availableAmount) {
      minTopLimit = Math.min(availableAmount, amount - 0.01);
    }

    return {
      paymentStatusId: payment.paymentStatusId,
      sourceTypeId: payment.sourceTypeId,
      parentPaymentId: payment.parentPaymentId,
      amount,
      availableAmount,
      hasAdjusment,
      maxDate: confirmDate.isSameOrAfter(paymentDate) ? confirmDate : paymentDate,
      paymentTransferLimit: {
        min: minTopLimit === 0 ? 0 : 0.01,
        max: minTopLimit,
      },
    };
  }, [payment, paymentAdjustments]);

  const submitTransferPaymentProcess = (values) => {
    setClickNextHasModal(false);
    startLoading();
    if (agreementForRebase?.agreementId === payment?.agreement.id) {
      message.destroy();
      return message.error("Перенос платежа на тот же самый договор запрещен");
    }

    const newAgreementNumber = agreementForRebase?.agreementNumber || "";
    const paramsRequest: {
      paymentId?: number;
      targetAgreementId?: number;
      transferAmount?: number;
      dsc?: string;
      transferDate?: string;
    } = {
      paymentId: payment?.id,
      targetAgreementId: agreementForRebase?.agreementId,
      transferAmount: values.transferAmount,
      dsc: values.reason,
    };

    if (values.transferDate) {
      const isDayNowSelect = values.transferDate.isSame(paymentData?.maxDate, "day");
      paramsRequest.transferDate = isDayNowSelect
        ? toHttpFormat(paymentData?.maxDate)
        : toHttpFormatStartOfDay(values.transferDate);
    }

    axios
      .post(`${baseUrl}/processes-api/processes/payment-transfer`, paramsRequest)
      .then(() => {
        closeActiveModal();
        message.destroy();
        Modal.warning({
          content:
            "Задание создано. При отсутствии изменений в течении 5 минут, пожалуйста обратитесь в службу технического сопровождения.",
        });
        setTimeout(() => {
          if (paymentId) {
            fetchPaymentAndAdjustments(paymentId);
          }
        }, 7e3);
      })
      .catch(() => {
        message.error(`Ошибка переноса платежа на договор ${newAgreementNumber}`);
      })
      .finally(() => {
        stopLoading();
        setAgreementForRebase(undefined);
        createPaymentTransferForm.resetFields();
      });
  };

  const submitTransferPayment = (values) => {
    setClickNextHasModal(false);
    startLoading();
    if (agreementForRebase?.agreementId === payment?.agreement.id) {
      message.destroy();
      return message.error("Перенос платежа на тот же самый договор запрещен");
    }

    const newAgreementNumber = agreementForRebase?.agreementNumber || "";

    axios
      .patch(`${baseUrl}/payments-api/payments/change-agreements`, {
        changeAgreements: [
          {
            paymentId: payment?.id,
            newAgreementId: agreementForRebase?.agreementId,
          },
        ],
        reason: values.reason,
      })
      .then(() => {
        message.success(`Платеж перенесен на договор ${newAgreementNumber}`);
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
        closeActiveModal();
      })
      .catch(() => {
        message.error(`Ошибка переноса платежа на договор ${newAgreementNumber}`);
      })
      .finally(() => {
        stopLoading();
        setAgreementForRebase(undefined);
        createPaymentTransferForm.resetFields();
      });
  };

  const submitCancelPayment = (reason?: string) => {
    startLoading();
    const url = `${baseUrl}/payments-api/payments/cancel`;
    axios
      .patch(url, {
        paymentIds: [payment!.id],
        reason,
      })
      .then(() => {
        message.success("Платеж отменен");
        closeActiveModal();
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка отмены платежа");
      })
      .finally(stopLoading);
  };

  const submitDeleteAdjusment = (transactionId: number) => {
    startLoading();
    const url = buildUrl({
      url: `${baseUrl}/billing-api/adj`,
      searchParams: {
        transactionId,
      },
    });
    return axios
      .delete(url)
      .then(() => {
        message.success("Корректировка удалена");
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка удаления корректировки");
      })
      .finally(stopLoading);
  };

  const submitCreateAdjusment = (values: CreateAdjustmentFormData, reasonContentIds?: number[]) => {
    startLoading();
    message.loading("Создание корректировки");
    const url = buildUrl({
      url: `${baseUrl}/processes-api/processes/payment-adjustment`,
    });
    const data: CreateAdjustmentData = {
      paymentId: payment!.id,
      adjustmentDate: toHttpFormat(moment()),
      dsc: values.dsc,
      adjustmentReasonId: values.adjustmentReasonId,
      adjustmentTypeId: values.adjustmentTypeId,
    };
    if (reasonContentIds) {
      data.reasonContentIds = reasonContentIds;
    }
    if (values.amount) {
      data.amount = values.amount;
    }
    if ([7, 8].includes(values.adjustmentTypeId)) {
      data.adjustmentAmount = payment?.amount;
    } else if (values.adjustmentAmount) {
      data.adjustmentAmount = values.adjustmentAmount;
    }
    axios
      .post(url, data)
      .then(() => {
        createAdjusmentForm.resetFields();
        closeActiveModal();
        message.destroy();
        Modal.warning({
          content:
            "Задание создано. При отсутствии изменений в течении 5 минут, пожалуйста обратитесь в службу технического сопровождения.",
          onOk: () => {
            setActiveModal("listAdjustment");
          },
        });
        setTimeout(() => {
          if (paymentId) {
            fetchPaymentAndAdjustments(paymentId);
          }
        }, 7e3);
      })
      .catch(() => {
        message.error("Ошибка создания корректировки");
      })
      .finally(stopLoading);
  };

  const uncoverGoal = (goalId: number | string) => {
    startLoading();
    const url = buildUrl({
      url: `${baseUrl}/payments-api/payments/uncover-goals`,
    });
    const body: UncoverGoalBody = {
      uncoverGoals: [{ goalId }],
    };
    return axios
      .patch(url, body)
      .then(() => {
        message.success("Отметка оплаты снята");
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка снятия отметки оплаты");
      })
      .finally(stopLoading);
  };

  const submitCreateBillGoal = (data: CreateGoalBillData) => {
    startLoading();
    const url = `${baseUrl}/payments-api/payments/cover-goal-bills`;
    axios
      .post(url, data)
      .then(() => {
        closeActiveModal();
        message.success("Цель создана");
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка создания цели");
      })
      .finally(stopLoading);
  };

  const submitCreateOfferGoal = (data: CreateGoalOfferData) => {
    startLoading();
    const url = `${baseUrl}/payments-api/payments/cover-goal-offerings`;
    axios
      .post(url, data)
      .then(() => {
        closeActiveModal();
        message.success("Цель создана");
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка создания цели");
      })
      .finally(stopLoading);
  };

  const submitCreateServiceGoal = (data: CreateGoalServiceData) => {
    startLoading();
    const url = `${baseUrl}/payments-api/payments/cover-goal-services`;
    axios
      .post(url, data)
      .then(() => {
        closeActiveModal();
        message.success("Цель создана");
        if (paymentId) {
          fetchPaymentAndAdjustments(paymentId);
        }
      })
      .catch(() => {
        message.error("Ошибка создания цели");
      })
      .finally(stopLoading);
  };

  const paymentHeader = useMemo(() => {
    if (!paymentData) {
      return null;
    }
    const {
      paymentStatusId,
      sourceTypeId,
      hasAdjusment,
      availableAmount,
      parentPaymentId,
    } = paymentData;
    return (
      <div className="account-payment-details-page__payment-header">
        <div>Платеж</div>
        <div className="account-payment-details-page__payment-header-btns">
          {paymentStatusId !== 4 &&
            sourceTypeId !== 128 &&
            ((availableAmount ?? 0) >= 0.01 || (availableAmount === 0 && !hasAdjusment)) &&
            hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) && (
              <Tooltip title="Перенести на договор">
                <RebaseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveModal("transferPayment");
                  }}
                  style={{ marginTop: "2px", marginRight: "12px" }}
                />
              </Tooltip>
            )}
          {paymentStatusId !== 4 &&
            parentPaymentId === null &&
            sourceTypeId !== 128 &&
            hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) &&
            !hasAdjusment && (
              <Tooltip title="Отменить платеж">
                <DeleteIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveModal("cancelPayment");
                  }}
                  style={{ marginTop: "3px" }}
                />
              </Tooltip>
            )}
          {hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) &&
            sourceTypeId !== 128 &&
            paymentStatusId !== 1 &&
            paymentStatusId !== 4 && (
              <Tooltip title="Корректировки платежа">
                <AdjustmentIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveModal("listAdjustment");
                  }}
                  color="#0C77C6"
                  style={{ marginTop: "-1px" }}
                />
              </Tooltip>
            )}
          <DotsIcon
            onClick={(e) => {
              e.stopPropagation();
              setActiveModal("history");
            }}
            style={{ marginRight: 0 }}
          />
        </div>
      </div>
    );
  }, [paymentData]);

  const goalsHeader = useMemo(() => {
    if (!paymentData) {
      return null;
    }
    const { availableAmount } = paymentData;
    return (
      <div className="account-payment-details-page__goals-header">
        <div>Цели платежа</div>
        <div className="account-payment-details-page__goals-header-btns">
          {hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) && (availableAmount ?? 0) > 0 && (
            <Tooltip title="Создать цель">
              <PlusIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveModal("createGoal");
                }}
                style={{ marginTop: "1px" }}
              />
            </Tooltip>
          )}
        </div>
      </div>
    );
  }, [paymentData]);

  return (
    <>
      <Spin spinning={checkIsLoading(paymentLoading, paymentAdjustmentsLoading)}>
        <div className={combineClasses("account-payment-details-page", className)}>
          <Breadcrumb className="breadcrumb" style={{ backgroundColor: "white" }}>
            <Breadcrumb.Item>
              <Link to="/">Аккаунты</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/accounts/${account.id}/info`}>{fullName}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/accounts/${account.id}/payments`}>Платежи</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Детали платежа</Breadcrumb.Item>
          </Breadcrumb>
          <h1>
            <Tooltip title="К списку платежей">
              <LeftOutlined
                onClick={() => {
                  const path = pathname.split("/").slice(0, 4).join("/");
                  history.push(path);
                }}
                className="account-payment-details-page__backBtn"
              />
            </Tooltip>
            Детали платежа
          </h1>
          <Divider className="account-payment-details-page__divider1" />
          <Collapse defaultActiveKey="1" bordered={false}>
            <Collapse.Panel key="1" header={paymentHeader}>
              <Divider className="account-payment-details-page__divider2" />
              <AccountPaymentDetails payment={payment} paymentAdjustments={paymentAdjustments} />
            </Collapse.Panel>
          </Collapse>
          <Collapse defaultActiveKey="1" bordered={false}>
            <Collapse.Panel key="1" header={goalsHeader}>
              <Divider className="account-payment-details-page__divider2" />
              <AccountPaymentGoals
                goals={payment?.goals}
                isLoading={isLoading}
                uncoverGoal={uncoverGoal}
              />
            </Collapse.Panel>
          </Collapse>
        </div>
      </Spin>
      <PaymentTransferModal
        maxDate={paymentData?.maxDate}
        createPaymentTransferForm={createPaymentTransferForm}
        visible={activeModal === "transferPayment"}
        closeModal={closeActiveModal}
        agreementForRebase={agreementForRebase}
        setAgreementForRebase={setAgreementForRebase}
        clickNextHasModal={clickNextHasModal}
        setClickNextHasModal={setClickNextHasModal}
        submitTransferPayment={submitTransferPayment}
        submitTransferPaymentProcess={submitTransferPaymentProcess}
        paymentTransferLimit={paymentData?.paymentTransferLimit}
        isLoading={isLoading}
      />
      <PaymentCancelModal
        closeModal={closeActiveModal}
        submitCancelPayment={submitCancelPayment}
        visible={activeModal === "cancelPayment"}
        isLoading={isLoading}
      />
      <AdjustmentsModal
        visible={activeModal === "listAdjustment"}
        closeModal={closeActiveModal}
        openCreateAdjusmentModal={() => {
          setActiveModal("createAdjustment");
        }}
        submitDeleteAdjusment={submitDeleteAdjusment}
        isLoading={isLoading}
      />
      <CreateAdjustmentsModal
        visible={activeModal === "createAdjustment"}
        closeModal={() => {
          setActiveModal("listAdjustment");
        }}
        isLoading={isLoading}
        createAdjusmentForm={createAdjusmentForm}
        submitCreateAdjusment={submitCreateAdjusment}
        startLoading={startLoading}
        stopLoading={stopLoading}
      />
      <CreateGoalModal
        visible={activeModal === "createGoal"}
        closeModal={closeActiveModal}
        isLoading={isLoading}
        submitCreateBillGoal={submitCreateBillGoal}
        submitCreateOfferGoal={submitCreateOfferGoal}
        submitCreateServiceGoal={submitCreateServiceGoal}
      />
      <HistoryAndPropertyModal
        objectTypeSysname="payment"
        objectId={payment?.id}
        handleHideModal={closeActiveModal}
        visibleModal={activeModal === "history"}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE)}
      />
    </>
  );
};
